import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter } from 'lucide-react';

const Footer = () => {
  const [contactInfo, setContactInfo] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await fetch(`${API_URL}/api/contact`);
        if (response.ok) {
          const data = await response.json();
          setContactInfo(data);
        }
      } catch (error) {
        console.error('Failed to fetch contact info:', error);
      }
    };
    fetchContactInfo();
  }, [API_URL]);

  // Use fetched data or fallback to defaults
  const companyName = contactInfo?.companyName || 'AEPL';
  const email = contactInfo?.email || 'contact@amical.co.in';
  const phone = contactInfo?.phone || '+91-XXXXXXXXXX';
  const address = contactInfo?.address || 'Maharashtra, India';

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        {/* Main Footer */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="text-3xl font-bold mb-4 text-cyan-400">
              {companyName.split('(')[0].trim()}
            </div>
            <p className="text-gray-400 leading-relaxed mb-6 max-w-md">
              Empowering governance through innovative technology solutions. Building stronger relationships between public servants and their constituents.
            </p>
            <div className="flex gap-4">
              <a href="#" className="w-10 h-10 rounded-full bg-gray-800 hover:bg-cyan-500 flex items-center justify-center transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-gray-800 hover:bg-cyan-500 flex items-center justify-center transition-colors">
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Company</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-gray-400 hover:text-cyan-400 transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-400 hover:text-cyan-400 transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/gramtek" className="text-gray-400 hover:text-cyan-400 transition-colors">
                  Gramtek
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-cyan-400 transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Solutions */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Solutions</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/products/pds" className="text-gray-400 hover:text-cyan-400 transition-colors">
                  PDS
                </Link>
              </li>
              <li>
                <Link to="/products/crs" className="text-gray-400 hover:text-cyan-400 transition-colors">
                  CRS
                </Link>
              </li>
              <li>
                <Link to="/products/kachurwahi" className="text-gray-400 hover:text-cyan-400 transition-colors">
                  Self-Reliance
                </Link>
              </li>
              <li>
                <Link to="/products/whatsapp" className="text-gray-400 hover:text-cyan-400 transition-colors">
                  WhatsApp
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-gray-400">
                <Mail className="w-5 h-5 mt-0.5 flex-shrink-0 text-cyan-400" />
                <a href={`mailto:${email}`} className="hover:text-cyan-400 transition-colors">
                  {email}
                </a>
              </li>
              <li className="flex items-start gap-3 text-gray-400">
                <Phone className="w-5 h-5 mt-0.5 flex-shrink-0 text-cyan-400" />
                <a href={`tel:${phone.replace(/[^0-9+]/g, '')}`} className="hover:text-cyan-400 transition-colors">
                  {phone}
                </a>
              </li>
              <li className="flex items-start gap-3 text-gray-400">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0 text-cyan-400" />
                <span className="whitespace-pre-line">{address.split('\n').pop()}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} {companyName}. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm text-gray-400">
              <a href="#" className="hover:text-cyan-400 transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-cyan-400 transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
